// The workshop's data: a pass cookie first (POST /api/v1/admin/workshop-pass: the files are plain loads, images and
// sounds included), then each pack's files from /workshop-media (functions/workshop-media, or the dev server's
// workshopFolder). The portal's pack sits at the root, the others under packs/<id>/ (edge/workshop.ts).
import { useQuery } from "@tanstack/react-query";
import { api, unwrap } from "@/shared/api/client";
import { WORKSHOP_PACK } from "../../../edge/workshop";

export { WORKSHOP_PACK };

export interface Pack { id: string; name: string; game?: string; hasWeapons?: boolean }

export const useWorkshopPass = () =>
  useQuery({
    queryKey: ["workshop-pass"],
    queryFn: async () => unwrap(await api.POST("/api/v1/admin/workshop-pass")),
    staleTime: 60 * 60_000, // the pass lasts two hours
    refetchInterval: 60 * 60_000,
    retry: false,
  });

/** The URL of a pack's file under /workshop-media. */
export function packUrl(pack: string, path: string): string {
  const file = path.split("/").map(encodeURIComponent).join("/");
  return pack === WORKSHOP_PACK ? `/workshop-media/${file}` : `/workshop-media/packs/${encodeURIComponent(pack)}/${file}`;
}

/** A file the sheet rendered for one character (media/<id>/<file>). */
export const mediaUrl = (pack: string, character: string, file: string): string => packUrl(pack, `media/${character}/${file}`);

/** For <img onError>: a file the sheet has not rendered (new monsters) leaves an empty frame, not a broken icon. */
export const hideMissing = (e: { currentTarget: HTMLImageElement }): void => {
  e.currentTarget.dataset.missing = "";
};

export class WorkshopFileError extends Error {
  constructor(message: string, readonly status: number) { super(message); }
}

export async function fetchJson<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "same-origin" });
  if (!r.ok) throw new WorkshopFileError((await r.text()) || r.statusText, r.status);
  return (await r.json()) as T;
}

export const isNotFound = (e: unknown): boolean => e instanceof WorkshopFileError && e.status === 404;

/** The packs the workshop offers: packs.json when published, else the portal's one pack. */
export function parsePacks(raw: unknown): Pack[] {
  const list = (raw as { packs?: unknown } | null)?.packs;
  const packs = Array.isArray(list)
    ? list.flatMap((p: { id?: unknown; name?: unknown; game?: unknown; gameName?: unknown; hasWeapons?: unknown }) => {
      if (typeof p?.id !== "string" || !p.id) return [];
      // The publish writes gameName (the launcher catalog's); the dev server's list writes game.
      const game = [p.gameName, p.game].find((g): g is string => typeof g === "string" && g !== "");
      return [{
        id: p.id, name: typeof p.name === "string" && p.name ? p.name : p.id,
        ...(game ? { game } : {}), ...(typeof p.hasWeapons === "boolean" ? { hasWeapons: p.hasWeapons } : {}),
      }];
    })
    : [];
  return packs.length ? packs : [{ id: WORKSHOP_PACK, name: WORKSHOP_PACK }];
}

export function usePacks() {
  const pass = useWorkshopPass();
  return useQuery({
    queryKey: ["workshop", "packs"],
    queryFn: async () => {
      try {
        return parsePacks(await fetchJson("/workshop-media/packs.json"));
      } catch (e) {
        if (isNotFound(e)) return parsePacks(null);
        throw e;
      }
    },
    enabled: pass.isSuccess,
    staleTime: 5 * 60_000,
    retry: false,
  });
}
